import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { generateUUID } from "@wix/form-schema";
import { useState, useMemo, useCallback } from "react";
import { useAddressAutofill } from "../../fields/multiline-address/multiline-address/address-autofill-context.js";
import { useFieldAttributes } from "../../services/field-attributes.js";
import { createOption, AutocompleteWUT } from "../autocomplete/autocomplete-wut.js";
import { FieldDescriptionWUT } from "../field-description/field-description-wut.js";
import { FieldErrorWUT } from "../field-error/field-error-wut.js";
import { FieldLabelWUT } from "../field-label/field-label-wut.js";
import { useJapanAutocomplete } from "./use-japan-autocomplete.js";
const allowAllOptions = () => true;
const PostalCodeInputWUT = ({
  id,
  target,
  label,
  hideLabel,
  placeholder,
  required,
  description,
  value,
  onChange,
  error,
  hasError = false,
  errorMessage,
  disabled,
  prefix,
  onBlur,
  onFocus,
  FieldLayout,
  requiredIndicatorType,
  requiredIndicatorPlacement
}) => {
  const { labelId, inputId, errorId, descriptionId, ariaDescribedBy } = useFieldAttributes(id, error, description);
  const { onAddressSelect } = useAddressAutofill();
  const [isAutocompleteEnabled, setIsAutocompleteEnabled] = useState(false);
  const { searchByPostalCode } = useJapanAutocomplete({
    isAutocompleteEnabled
  });
  const [prediction, setPrediction] = useState();
  const options = useMemo(
    () => prediction ? [
      createOption({
        id: generateUUID(),
        value: prediction.formattedAddress
      })
    ] : [],
    [prediction]
  );
  const handleChange = useCallback(
    async (event) => {
      const newValue = event.target.value;
      onChange(newValue);
      const address = await searchByPostalCode(newValue ?? "");
      setPrediction(address);
    },
    [onChange, searchByPostalCode]
  );
  const handleSelect = useCallback(async () => {
    setPrediction(void 0);
    if (prediction && onAddressSelect) {
      onAddressSelect(prediction);
    }
  }, [onAddressSelect, prediction]);
  const handleManualInput = useCallback(() => {
    setPrediction(void 0);
  }, []);
  const handleBlur = useCallback(
    (event) => {
      const newValue = event.target.value;
      setPrediction(void 0);
      onChange(newValue);
      onBlur?.();
    },
    [onChange, onBlur]
  );
  const handleFocus = useCallback(() => {
    setIsAutocompleteEnabled(true);
    onFocus?.();
  }, [onFocus]);
  return /* @__PURE__ */ jsx(
    FieldLayout,
    {
      fieldId: id,
      renderLabel: () => /* @__PURE__ */ jsx(
        FieldLabelWUT,
        {
          labelId,
          htmlFor: inputId,
          required,
          label,
          hideLabel,
          requiredIndicatorType,
          requiredIndicatorPlacement
        }
      ),
      renderInput: () => /* @__PURE__ */ jsx(
        AutocompleteWUT,
        {
          id: inputId,
          value,
          options,
          disabled,
          placeholder,
          prefix,
          error: hasError,
          filterPredicate: allowAllOptions,
          onChange: handleChange,
          onSelect: handleSelect,
          onManualInput: handleManualInput,
          onBlur: handleBlur,
          onFocus: handleFocus,
          ariaDescribedBy
        }
      ),
      renderDescription: () => /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(
          FieldErrorWUT,
          {
            target,
            hasError,
            errorMessage,
            id: errorId
          }
        ),
        /* @__PURE__ */ jsx(FieldDescriptionWUT, { description, id: descriptionId })
      ] })
    }
  );
};
export {
  PostalCodeInputWUT as default
};
//# sourceMappingURL=postal-code-input-wut.js.map
